import { useWorkspaceText } from '../interface-copy';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import workspaceFeedbackCopy from '@/i18n/ns/workspace-feedback';
import { useWorkspace } from '../context';
import { agentsApi, keepsAccess, type Agent } from './api';
import { CLOSES_CANVAS, useIsCompanyAdmin, when, type Act } from './shared';
import { ArchiveAction } from './archive-action';

// Editors, edit-access requests, admin controls and Archive, within the Agent
// Share sheet. The surrounding sheet owns audience and connection grants.

export function AccessPanel({ agent, busy, act }: { agent: Agent; busy: boolean; act: Act }) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const feedback = useNamespaceCopy(workspaceFeedbackCopy);
  const ws = useWorkspace();
  const editors = (agent.editors ?? []).filter((editor) => editor.status === 'granted');
  const requests = (agent.editors ?? []).filter((editor) => editor.status === 'requested');
  const admin = useIsCompanyAdmin();
  const listsEditors = agent.scope === 'company' && agent.editors != null;
  return (
    <>
      <p className="ws-agents-section-title">{uiText('Owner')}</p>
      <div className="ws-agents-people">
        <div className="ws-agents-person">
          <span>
            <strong>
              {agent.owner?.name ?? uiText('{value1} · admins', { value1: ws.companyName })}
            </strong>
            <small>
              {agent.owner ? uiText('Owner · always edits') : uiText('Admins manage it')}
            </small>
          </span>
        </div>
      </div>
      {/* The server sends editors only to people who can edit; readers see no list. */}
      {listsEditors && <p className="ws-agents-section-title">{uiText('Editors')}</p>}
      <div className="ws-agents-people" hidden={!listsEditors}>
        {editors.map((editor) => (
          <div className="ws-agents-person" key={editor.userId}>
            <span>
              <strong>{editor.name ?? uiText('A colleague')}</strong>
              <small>{uiText('Can edit drafts and publish versions')}</small>
            </span>
            {agent.manages && (
              <button
                className="ws-text-button"
                disabled={busy}
                onClick={() =>
                  void act(
                    () => agentsApi.removeEditor(agent.id, editor.userId),
                    formatCopy(feedback.agentEditorRemoved, {
                      name: editor.name ?? feedback.agentThey,
                    }),
                  )
                }
              >
                {uiText('Remove')}
              </button>
            )}
          </div>
        ))}
        {!editors.length && <p className="ws-muted">{uiText('No editors yet.')}</p>}
        {agent.scope === 'company' && (
          <p className="ws-muted">{uiText('Admins can always edit company agents.')}</p>
        )}
      </div>
      {agent.manages && requests.length > 0 && (
        <>
          <p className="ws-agents-section-title">{uiText('Asking for edit access')}</p>
          <div className="ws-agents-people">
            {requests.map((request) => (
              <div className="ws-agents-person" key={request.userId}>
                <span>
                  <strong>{request.name ?? uiText('A colleague')}</strong>
                  <small>
                    {uiRich('Asked {value1} · also in your Inbox', {
                      value1: when(request.requestedAt, uiText.locale),
                    })}
                  </small>
                </span>
                <span className="ws-actions">
                  <button
                    className="ws-button primary small"
                    disabled={busy}
                    onClick={() =>
                      void act(
                        () => agentsApi.decideEditAccess(agent.id, request.userId, true),
                        formatCopy(feedback.agentEditGranted, {
                          name: request.name ?? feedback.agentThey,
                        }),
                      )
                    }
                  >
                    {uiText('Grant')}
                  </button>
                  <button
                    className="ws-button small"
                    disabled={busy}
                    onClick={() =>
                      void act(
                        () => agentsApi.decideEditAccess(agent.id, request.userId, false),
                        formatCopy(feedback.agentEditDeclined, {
                          name: request.name ?? feedback.agentThey,
                        }),
                      )
                    }
                  >
                    {uiText('Decline')}
                  </button>
                </span>
              </div>
            ))}
          </div>
        </>
      )}
      {admin && (
        <>
          <p className="ws-agents-section-title">{uiText('Admin')}</p>
          <div className="ws-actions">
            {/* Personal means only its owner uses it: a company-owned agent has none. */}
            {agent.scope === 'company' && agent.owner && (
              <button
                className="ws-button"
                disabled={busy}
                onClick={() =>
                  void act(
                    // An admin who is not its owner can no longer open it.
                    async () =>
                      keepsAccess(await agentsApi.patch(agent.id, { scope: 'personal' }))
                        ? undefined
                        : CLOSES_CANVAS,
                    agent.isOwner
                      ? uiText('Made personal and switched off. Switch it on when you are ready.')
                      : uiText(
                          'Made personal and switched off. Its owner is told which version is live.',
                        ),
                  )
                }
              >
                {uiText('Make personal')}
              </button>
            )}
            {agent.enabled && (
              <button
                className="ws-button"
                disabled={busy}
                onClick={() =>
                  void act(
                    () => agentsApi.patch(agent.id, { enabled: false }),
                    uiText('Switched off.'),
                  )
                }
              >
                {uiText('Switch off')}
              </button>
            )}
          </div>
        </>
      )}
      {agent.manages && <ArchiveAction agent={agent} busy={busy} act={act} />}
    </>
  );
}
